import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import Toast from "../../components/Common/Toast";
import Pagination from "../../components/Common/Pagination";
import commonService, { normalizeListResponse } from "../../services/otherServices";
import { getErrorMessage } from "../../services/apiClient";
import { useAuthContext } from "../../contexts/AuthContext";
import { isAdminRole } from "../../utils/roles";
import { formatIST } from "../../utils/dateHelpers";
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  Power,
  PowerOff,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const PAGE_SIZE = 10;

// Matches the role options already offered on sign-up (SignUpPage.tsx).
const ROLES = ["Admin", "Manager", "Segment"];

const emptyForm = { fname: "", email: "", mobile: "", role: "Segment", password: "" };

const AdminUsersPage: React.FC = () => {
  const { user: currentUser } = useAuthContext();

  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [hasReliableTotal, setHasReliableTotal] = useState(true);

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [form, setForm] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const [confirmDeleteUser, setConfirmDeleteUser] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [busyUserId, setBusyUserId] = useState<number | null>(null);

  const fetchUsers = async () => {
    setLoading(true);

    try {
      const res = await commonService.getUsers({ search, page, limit: PAGE_SIZE });
      const { items, total, totalPages: pages, hasReliableTotal: reliable } = normalizeListResponse(res);

      setUsers(items);
      setTotalRecords(total);
      setTotalPages(pages);
      setHasReliableTotal(reliable);
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to load users") });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput);
  };

  const openAddModal = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setShowFormModal(true);
  };

  const openEditModal = (u: any) => {
    setEditingUser(u);
    setForm({ fname: u.fname, email: u.email, mobile: u.mobile, role: u.role, password: "" });
    setShowFormModal(true);
  };

  const handleSave = async () => {
    if (!form.fname.trim() || !form.email.trim() || !form.mobile.trim()) {
      setToast({ type: "error", message: "Name, email and mobile are required" });
      return;
    }

    if (!editingUser && !form.password.trim()) {
      setToast({ type: "error", message: "Password is required for a new user" });
      return;
    }

    setIsSaving(true);

    try {
      if (editingUser) {
        await commonService.updateUser(editingUser.id, {
          fname: form.fname,
          email: form.email,
          mobile: form.mobile,
          role: form.role,
        });
        setToast({ type: "success", message: "User updated" });
      } else {
        await commonService.createUser({
          fname: form.fname,
          email: form.email,
          mobile: form.mobile,
          role: form.role,
          password: form.password,
          created_by: currentUser?.email,
        });
        setToast({ type: "success", message: "User created" });
      }

      setShowFormModal(false);
      fetchUsers();
    } catch (error) {
      setToast({
        type: "error",
        message: getErrorMessage(error, editingUser ? "Failed to update user" : "Failed to create user"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (u: any) => {
    setBusyUserId(u.id);

    try {
      if (u.is_active) {
        await commonService.deactivateUser(u.id);
        setToast({ type: "success", message: `${u.fname} deactivated` });
      } else {
        await commonService.activateUser(u.id);
        setToast({ type: "success", message: `${u.fname} activated` });
      }
      fetchUsers();
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to update user status") });
    } finally {
      setBusyUserId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteUser) return;

    setIsDeleting(true);

    try {
      await commonService.deleteUser(confirmDeleteUser.id);
      setToast({ type: "success", message: `${confirmDeleteUser.fname} deleted` });
      setConfirmDeleteUser(null);
      fetchUsers();
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to delete user") });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div style={{ padding: "28px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "22px",
          }}
        >
          <div>
            <div style={{ fontSize: "26px", fontWeight: 800, color: "#111827" }}>User Management</div>
            <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px" }}>
              {totalRecords} account{totalRecords === 1 ? "" : "s"} across the platform
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "9px 14px",
              }}
            >
              <Search size={15} color="#9ca3af" />
              <input
                placeholder="Search name / email"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                style={{ border: "none", outline: "none", fontSize: "13px", width: "180px" }}
              />
            </div>

            <button onClick={handleSearch} style={secondaryBtn}>
              Search
            </button>

            <button onClick={openAddModal} style={primaryBtn}>
              <Plus size={15} />
              Add User
            </button>
          </div>
        </div>

        <div
          style={{
            background: "white",
            borderRadius: "18px",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Mobile</th>
                  <th style={thStyle}>Role</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td style={tdStyle} colSpan={7}>
                      Loading users...
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td style={tdStyle} colSpan={7}>
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id}>
                      <td style={tdStyle}>
                        <div style={{ fontWeight: 600, color: "#111827" }}>{u.fname}</div>
                      </td>
                      <td style={tdStyle}>{u.email}</td>
                      <td style={tdStyle}>{u.mobile}</td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            padding: "4px 10px",
                            borderRadius: "999px",
                            fontSize: "11px",
                            fontWeight: 700,
                            ...roleBadgeStyle(u.role),
                          }}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span
                          style={{
                            padding: "4px 10px",
                            borderRadius: "999px",
                            fontSize: "11px",
                            fontWeight: 700,
                            background: u.is_active ? "#dcfce7" : "#fee2e2",
                            color: u.is_active ? "#15803d" : "#b91c1c",
                          }}
                        >
                          {u.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {u.created_at ? formatIST(u.created_at, { day: "2-digit", month: "2-digit", year: "numeric" }) : "-"}
                      </td>
                      <td style={tdStyle}>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button
                            onClick={() => openEditModal(u)}
                            title="Edit"
                            style={iconBtn}
                          >
                            <Pencil size={14} />
                          </button>

                          <button
                            onClick={() => handleToggleActive(u)}
                            disabled={busyUserId === u.id}
                            title={u.is_active ? "Deactivate" : "Activate"}
                            style={{
                              ...iconBtn,
                              color: u.is_active ? "#b45309" : "#15803d",
                              opacity: busyUserId === u.id ? 0.5 : 1,
                            }}
                          >
                            {u.is_active ? <PowerOff size={14} /> : <Power size={14} />}
                          </button>

                          <button
                            onClick={() => setConfirmDeleteUser(u)}
                            title="Delete"
                            disabled={currentUser?.email === u.email}
                            style={{
                              ...iconBtn,
                              color: "#dc2626",
                              opacity: currentUser?.email === u.email ? 0.4 : 1,
                              cursor: currentUser?.email === u.email ? "not-allowed" : "pointer",
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {hasReliableTotal && totalPages > 1 ? (
            <div style={{ padding: "16px 18px" }}>
              <Pagination
                page={page}
                total_pages={totalPages}
                total_records={totalRecords}
                page_size={PAGE_SIZE}
                onPageChange={setPage}
              />
            </div>
          ) : !hasReliableTotal && !loading ? (
            // No confirmed cross-page total for this endpoint yet — Prev/Next
            // only, gated on whether this page was full (see AdminLogsPage).
            <div
              style={{
                padding: "14px 18px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                {users.length} user{users.length === 1 ? "" : "s"} on this page
              </span>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  style={{ ...secondaryBtn, opacity: page <= 1 ? 0.5 : 1 }}
                >
                  <ChevronLeft size={14} />
                  Prev
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={users.length < PAGE_SIZE}
                  style={{ ...secondaryBtn, opacity: users.length < PAGE_SIZE ? 0.5 : 1 }}
                >
                  Next
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* ADD/EDIT MODAL */}
      {showFormModal && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <div style={modalHeader}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#111827" }}>
                {editingUser ? "Edit User" : "Add User"}
              </h3>
              <button onClick={() => setShowFormModal(false)} style={closeBtn}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: "22px", display: "flex", flexDirection: "column", gap: "14px" }}>
              <Field label="Full Name">
                <input
                  value={form.fname}
                  onChange={(e) => setForm({ ...form, fname: e.target.value })}
                  style={inputStyle}
                />
              </Field>

              <Field label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  style={inputStyle}
                />
              </Field>

              <Field label="Mobile">
                <input
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                  style={inputStyle}
                />
              </Field>

              <Field label="Role">
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  style={inputStyle}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </Field>

              {!editingUser && (
                <Field label="Password">
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    style={inputStyle}
                  />
                </Field>
              )}
            </div>

            <div style={modalFooter}>
              <button onClick={() => setShowFormModal(false)} style={secondaryBtn}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={isSaving} style={primaryBtn}>
                {isSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM MODAL */}
      {confirmDeleteUser && (
        <div style={modalOverlay}>
          <div style={{ ...modalCard, maxWidth: "420px" }}>
            <div style={modalHeader}>
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#111827" }}>
                Delete User
              </h3>
              <button onClick={() => setConfirmDeleteUser(null)} style={closeBtn}>
                <X size={18} />
              </button>
            </div>

            <div style={{ padding: "22px", fontSize: "14px", color: "#374151" }}>
              This permanently deletes <strong>{confirmDeleteUser.fname}</strong> (
              {confirmDeleteUser.email}). This can't be undone.
            </div>

            <div style={modalFooter}>
              <button onClick={() => setConfirmDeleteUser(null)} style={secondaryBtn}>
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                style={{ ...primaryBtn, background: "#dc2626" }}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </AdminLayout>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px", fontWeight: 600 }}>
      {label}
    </div>
    {children}
  </div>
);

const roleBadgeStyle = (role: string): React.CSSProperties => {
  if (isAdminRole(role)) return { background: "#ede9fe", color: "#6d28d9" };
  if (role === "Manager") return { background: "#fef3c7", color: "#b45309" };
  return { background: "#e0f2fe", color: "#0369a1" };
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "13px 16px",
  fontSize: "11px",
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  borderBottom: "1px solid #e5e7eb",
};

const tdStyle: React.CSSProperties = {
  padding: "14px 16px",
  fontSize: "13px",
  color: "#374151",
  borderBottom: "1px solid #f3f4f6",
};

const iconBtn: React.CSSProperties = {
  width: "30px",
  height: "30px",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  background: "#fff",
  color: "#374151",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  background: "#4338ca",
  color: "#fff",
  border: "none",
  borderRadius: "10px",
  padding: "10px 16px",
  fontWeight: 600,
  fontSize: "13px",
  cursor: "pointer",
};

const secondaryBtn: React.CSSProperties = {
  background: "#f3f4f6",
  color: "#111827",
  border: "none",
  borderRadius: "10px",
  padding: "10px 16px",
  fontWeight: 600,
  fontSize: "13px",
  cursor: "pointer",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  outline: "none",
  fontSize: "14px",
  boxSizing: "border-box",
};

const modalOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.55)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  backdropFilter: "blur(4px)",
};

const modalCard: React.CSSProperties = {
  width: "100%",
  maxWidth: "480px",
  background: "#fff",
  borderRadius: "18px",
  overflow: "hidden",
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
};

const modalHeader: React.CSSProperties = {
  padding: "18px 22px",
  borderBottom: "1px solid #e5e7eb",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

const modalFooter: React.CSSProperties = {
  padding: "18px 22px",
  borderTop: "1px solid #e5e7eb",
  display: "flex",
  justifyContent: "flex-end",
  gap: "10px",
};

const closeBtn: React.CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "#6b7280",
};

export default AdminUsersPage;
